import React, { FC } from 'react';
import { RichText, Text, Placeholder } from '@sitecore-jss/sitecore-jss-react';
import InfoTooltip from '../../../@shared/infoToolTip';
import { THeroSectionTextProps } from '../../types/heroSectionText.types';
import { HeroSectionTextModel } from '../../models/heroSectionText.model';
import { withTranslation, WithTranslation } from 'react-i18next';

import './styles.scss';
import CTA from '../../../@shared/CTA';
import ctaTypes from '../../../../enums/CTA.enums';
import { targetLinkdsk } from '../../../../utils/targetDskLink';
import useMediaQuery from '../../../../hooks/useMediaQuery';
import breakpoints from '../../../../constants/breakpoints.constants';
import { googleButtonClickEvent } from '../../../../utils/analytics';
import { modelOverviewCtaClick, primaryCTA, secondaryCTA } from '../../../../constants/analytics.constants';
import useSitecoreContext from '../../../../hooks/useSitecoreContext';
import useGetBuildAndPriceLink from '../../../../hooks/useGetBuildAndPriceLink';
import { appendRootToInternalLink } from '../../../../utils/appendRootToInternalLink.utils';
import DictionaryTerm from '../../../@shared/dictionaryTerm';

const HeroSectionText: FC<WithTranslation & THeroSectionTextProps> = (props) => {
  const model = new HeroSectionTextModel(props);
  const { t } = props;
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints['tbl-p']}px)`);
  const {sitecoreContext} = useSitecoreContext();
  const productLine = sitecoreContext?.analytics?.['productLine'] ? sitecoreContext?.analytics?.['productLine'] : undefined;
  const productSeries = sitecoreContext?.analytics?.['productSeries'] ? sitecoreContext?.analytics?.['productSeries'] : undefined;
  const productModel = sitecoreContext?.analytics?.['productModel'] ? sitecoreContext?.analytics?.['productModel'] : undefined;
  const buildAndPriceUrl = useGetBuildAndPriceLink('model');

  return (
    <div className="product-hero-section-text">
      <Text field={model.title} tag="h1" className="product-hero-section-text__title" editable={false} />
      <div className="product-hero-section-text__starting-price">
        {model.msrp && model.msrp.value && (
          <div className="product-hero-section-text__msrp-tooltip">
            <span className="">
              {t('cnhi-startingFrom')} <RichText field={model.msrp} className="product-hero-section-text__msrp" />
            </span>
            <InfoTooltip showIcon={true} width={300}>
              <div className="product-hero-section-text__tooltip__wrapper">
                <Text
                  field={{ value: t('cnhi-MsrpTooltipTitle') }}
                  tag="h2"
                  className="product-hero-section-text__tooltip__title"
                  editable={false}
                />
                <Text
                  field={{ value: t('cnhi-MsrpTooltipDescription') }}
                  tag="div"
                  className="product-hero-section-text__tooltip__body"
                  editable={false}
                />
              </div>
            </InfoTooltip>
          </div>
        )}
      </div>

      <RichText field={model.description} tag="div" className="product-hero-section-text__description rich-text" editable={false} />
      {
        model.enableBuildPriceCTA && buildAndPriceUrl && (
          <CTA
            type={ctaTypes.primaryOrange}
            extraClassName="product-hero-section-text__link product-hero-section-text__link--primary product-hero-section-text__build-price"
            href={appendRootToInternalLink(buildAndPriceUrl)}
          >
            <DictionaryTerm keyword={'cnhi-build-price'} />
          </CTA>
        )
      }
      <div className={'product-hero-section-text--cta-section'}>
        {model?.primaryLink?.label && model?.primaryLink?.url && (
          <span className={'product-hero-section-text__link product-hero-section-text__link--primary'}>
            <CTA
              type={ctaTypes.primaryOrange}
              field={{
                value: {
                  href: model.primaryCTAUrl,
                  text: model.primaryLink.label,
                },
              }}
              onClick={() => {
                googleButtonClickEvent(modelOverviewCtaClick, model?.primaryLink?.label, primaryCTA, productLine, productSeries, productModel);
              }}
              target={targetLinkdsk({ value: { href: model?.primaryLink?.url, target: model?.primaryLink?.target } }, isDesktop)}
            />
          </span>
        )}
        <div className={'product-hero-section-text__link--popup '} onClick={() =>
          googleButtonClickEvent(modelOverviewCtaClick, model?.placeholderCtaTitle, secondaryCTA, productLine, productSeries, productModel)}>
          <Placeholder name={'jss-generic-popup'} rendering={props.rendering} />
        </div>
      </div>

      {/* Secondary CTA isn't needed for version - 1(124054, 139561) */}
      {/* {model?.secondaryLink?.label && model?.secondaryLink?.url && (
        <span className={"product-hero-section-text__link product-hero-section-text__link--secondary"}>
          <RoutableNotEditableLink href={model.secondaryLink.url} className={'product-hero-section-text__link--secondary-link-text'}>
            {model.secondaryLink.label}
          </RoutableNotEditableLink>
          <pfe-icon
            icon="dcx-download"
            size="24"
            color="complement"
          ></pfe-icon>
        </span>
      )} */}

      {model?.summarySpecs?.length > 0 && (
        <div className="product-hero-section-text__quick-spec-wrapper">
          {model?.summarySpecs?.map((spec, index) => (
            <div className="product-hero-section-text__quick-spec-item" key={`${spec.key}-${index}`}>
              <RichText
                field={spec.key}
                tag="div"
                className="product-hero-section-text__quick-spec-item--title rich-text"
                editable={false}
              />
              <RichText
                field={spec.value}
                tag="div"
                className="product-hero-section-text__quick-spec-item--value rich-text"
                editable={false}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default withTranslation()(HeroSectionText);
